import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, interval } from 'rxjs';
import { startWith, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Numeric values mirror the backend enums exactly (ClearScrap.Common.Model.Entity.ReportType /
// ReportJobStatus) — the API serializes enums as their underlying number, not their name.
export enum ReportType {
  AgentInsights = 0,
  CustomerCollections = 1
}

export enum ReportJobStatus {
  Pending = 0,
  Running = 1,
  Completed = 2,
  Failed = 3,
  Expired = 4
}

export interface ReportJobStatusDto {
  id: string;
  reportType: ReportType;
  status: ReportJobStatus;
  statusMessage?: string;
  fileName?: string;
  rowCount?: number;
  fromDate: string;
  toDate: string;
  createdAt: string;
  completedAt?: string;
  expiresAt?: string;
  downloadUrl?: string;
}

export interface DateRangeReportFilter {
  fromDate: string;
  toDate: string;
  geoZoneIds?: string[];
}

@Injectable({ providedIn: 'root' })
export class ReportService {
  private readonly baseUrl = `${environment.apiConfig.uri}/api/Reports`;

  constructor(private http: HttpClient) { }

  startAgentInsightsReport(filter: DateRangeReportFilter): Observable<ReportJobStatusDto> {
    return this.http.post<ReportJobStatusDto>(`${this.baseUrl}/agent-insights`, this.toRequestBody(filter));
  }

  startCustomerCollectionsReport(filter: DateRangeReportFilter): Observable<ReportJobStatusDto> {
    return this.http.post<ReportJobStatusDto>(`${this.baseUrl}/customer-collections`, this.toRequestBody(filter));
  }

  getJobStatus(jobId: string): Observable<ReportJobStatusDto> {
    return this.http.get<ReportJobStatusDto>(`${this.baseUrl}/jobs/${jobId}`);
  }

  /** Recent jobs of a given type, newest first — lets the UI resume tracking after a page reload. */
  getRecentJobs(reportType: ReportType, limit = 10): Observable<ReportJobStatusDto[]> {
    const params = new HttpParams().set('reportType', reportType).set('limit', limit);
    return this.http.get<ReportJobStatusDto[]>(`${this.baseUrl}/jobs`, { params });
  }

  /** Polls job status every `intervalMs` until it reaches a terminal status (Completed/Failed/Expired). */
  pollJobStatus(jobId: string, intervalMs = 3000): Observable<ReportJobStatusDto> {
    return interval(intervalMs).pipe(
      startWith(0),
      switchMap(() => this.getJobStatus(jobId)),
      takeWhile(status => status.status === ReportJobStatus.Pending || status.status === ReportJobStatus.Running, true)
    );
  }

  private toRequestBody(filter: DateRangeReportFilter) {
    return {
      fromDate: filter.fromDate,
      toDate: filter.toDate,
      geoZoneIds: filter.geoZoneIds && filter.geoZoneIds.length > 0 ? filter.geoZoneIds.join(',') : undefined
    };
  }
}
